export const ATOMIC_CHIP_CARET_MARKER = '\u200B';

export type AtomicChipPredicate = (node: Node | null) => node is HTMLElement;

type AtomicChipCaretSpacer = {
  chip: HTMLElement;
  node: Text;
  text: string;
};

export const ensureAtomicChipCaretSpacers = ({
  root,
  isChip,
}: {
  root: HTMLElement;
  isChip: AtomicChipPredicate;
}): void => {
  Array.from(root.querySelectorAll<HTMLElement>('*'))
    .filter(isChip)
    .forEach((chip) => {
      if (getAtomicChipCaretSpacer(chip.nextSibling, isChip)) return;
      chip.parentNode?.insertBefore(document.createTextNode(ATOMIC_CHIP_CARET_MARKER), chip.nextSibling);
    });
};

export const handlePlainArrowAroundAtomicChipBoundary = ({
  key,
  selection,
  editor,
  isChip,
}: {
  key: 'ArrowLeft' | 'ArrowRight';
  selection: Selection | null;
  editor: HTMLElement | null;
  isChip: AtomicChipPredicate;
}): boolean => {
  if (!selection?.isCollapsed || !editor || !selection.focusNode || !editor.contains(selection.focusNode)) return false;

  const { focusNode } = selection;
  const focusOffset = getSelectionFocusOffset(selection);
  if (focusNode === editor) {
    return moveCaretAtEditorBoundary({ key, selection, editor, isChip, focusOffset });
  }

  if (focusNode.nodeType !== Node.TEXT_NODE) return false;

  return moveCaretAtTextBoundary({
    key,
    selection,
    node: focusNode as Text,
    focusOffset,
    isChip,
  });
};

export const selectAtomicChipForNativeDeletion = ({
  direction,
  selection,
  editor,
  isChip,
}: {
  direction: 'backward' | 'forward';
  selection: Selection | null;
  editor: HTMLElement | null;
  isChip: AtomicChipPredicate;
}): boolean => {
  if (!selection?.isCollapsed || !selection.focusNode || !editor?.contains(selection.focusNode)) return false;

  const focusOffset = getSelectionFocusOffset(selection);
  const chip =
    direction === 'backward'
      ? getChipBeforeFocus({ focusNode: selection.focusNode, focusOffset, editor, isChip })
      : getChipAfterFocus({ focusNode: selection.focusNode, focusOffset, editor, isChip });
  if (!chip) return false;

  const range = document.createRange();
  range.setStartBefore(chip);

  const spacer = getAtomicChipCaretSpacer(chip.nextSibling, isChip);
  if (spacer) {
    range.setEnd(spacer.node, ATOMIC_CHIP_CARET_MARKER.length);
  } else {
    range.setEndAfter(chip);
  }

  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

export const stripAtomicChipCaretMarkers = (text: string, caretPos: number): { text: string; caretPos: number } => {
  let cleanText = '';
  let cleanCaretPos = caretPos;

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] === ATOMIC_CHIP_CARET_MARKER) {
      if (index < caretPos) cleanCaretPos -= 1;
      continue;
    }
    cleanText += text[index];
  }

  return { text: cleanText, caretPos: cleanCaretPos };
};

const getAtomicChipCaretSpacer = (node: Node | null, isChip: AtomicChipPredicate): AtomicChipCaretSpacer | null => {
  if (node?.nodeType !== Node.TEXT_NODE) return null;
  const chip = node.previousSibling;
  if (!isChip(chip)) return null;

  const text = getNodeText(node);
  if (!text.startsWith(ATOMIC_CHIP_CARET_MARKER)) return null;

  return { chip, node: node as Text, text };
};

const getStandaloneAtomicChipCaretSpacer = (node: Node | null, isChip: AtomicChipPredicate) => {
  const spacer = getAtomicChipCaretSpacer(node, isChip);
  return spacer?.text === ATOMIC_CHIP_CARET_MARKER ? spacer : null;
};

const moveCaretAtEditorBoundary = ({
  key,
  selection,
  editor,
  isChip,
  focusOffset,
}: {
  key: 'ArrowLeft' | 'ArrowRight';
  selection: Selection;
  editor: HTMLElement;
  isChip: AtomicChipPredicate;
  focusOffset: number;
}): boolean => {
  const previous = editor.childNodes[focusOffset - 1];
  const next = editor.childNodes[focusOffset];

  if (key === 'ArrowLeft') {
    const previousSpacer = getStandaloneAtomicChipCaretSpacer(previous, isChip);
    if (previousSpacer) {
      setCaretBeforeAtomicChip(selection, previousSpacer.chip);
      return true;
    }
    if (isChip(previous)) {
      setCaretBeforeAtomicChip(selection, previous);
      return true;
    }
  }

  if (key === 'ArrowRight') {
    if (isChip(next)) {
      setCaretAfterAtomicChip(selection, next, isChip);
      return true;
    }

    const nextSpacer = getStandaloneAtomicChipCaretSpacer(next, isChip);
    if (nextSpacer) {
      moveCaretRightFromAtomicChip(selection, nextSpacer, isChip);
      return true;
    }
  }

  return false;
};

const moveCaretAtTextBoundary = ({
  key,
  selection,
  node,
  focusOffset,
  isChip,
}: {
  key: 'ArrowLeft' | 'ArrowRight';
  selection: Selection;
  node: Text;
  focusOffset: number;
  isChip: AtomicChipPredicate;
}): boolean => {
  const text = getNodeText(node);
  const chipSpacer = getAtomicChipCaretSpacer(node, isChip);
  const standaloneSpacer = getStandaloneAtomicChipCaretSpacer(node, isChip);
  const chipAfterCaret = focusOffset === text.length && isChip(node.nextSibling) ? node.nextSibling : null;
  const chipBeforeCaret = getChipBeforeTextSelection({ node, focusOffset, isChip });

  if (chipAfterCaret && key === 'ArrowRight') {
    setCaretAfterAtomicChip(selection, chipAfterCaret, isChip);
    return true;
  }

  if (chipBeforeCaret && key === 'ArrowLeft') {
    setCaretBeforeAtomicChip(selection, chipBeforeCaret);
    return true;
  }

  if (standaloneSpacer) {
    if (key === 'ArrowLeft') {
      setCaretBeforeAtomicChip(selection, standaloneSpacer.chip);
    } else {
      moveCaretRightFromAtomicChip(selection, standaloneSpacer, isChip);
    }
    return true;
  }

  if (chipSpacer && key === 'ArrowRight' && focusOffset === 0) {
    setCaretAt(selection, node, ATOMIC_CHIP_CARET_MARKER.length);
    return true;
  }

  if (chipSpacer && key === 'ArrowRight' && focusOffset === ATOMIC_CHIP_CARET_MARKER.length) {
    moveCaretRightFromAtomicChip(selection, chipSpacer, isChip);
    return true;
  }

  if (chipSpacer && key === 'ArrowLeft' && focusOffset === ATOMIC_CHIP_CARET_MARKER.length) {
    setCaretBeforeAtomicChip(selection, chipSpacer.chip);
    return true;
  }

  return false;
};

const getChipBeforeFocus = ({
  focusNode,
  focusOffset,
  editor,
  isChip,
}: {
  focusNode: Node;
  focusOffset: number;
  editor: HTMLElement;
  isChip: AtomicChipPredicate;
}): HTMLElement | null => {
  if (focusNode === editor) return getChipBeforeEditorOffset(editor, focusOffset, isChip);
  if (focusNode.nodeType !== Node.TEXT_NODE) return null;

  const spacer = getAtomicChipCaretSpacer(focusNode, isChip);
  if (spacer && focusOffset <= ATOMIC_CHIP_CARET_MARKER.length) return spacer.chip;
  return getChipBeforeTextSelection({ node: focusNode as Text, focusOffset, isChip });
};

const getChipAfterFocus = ({
  focusNode,
  focusOffset,
  editor,
  isChip,
}: {
  focusNode: Node;
  focusOffset: number;
  editor: HTMLElement;
  isChip: AtomicChipPredicate;
}): HTMLElement | null => {
  if (focusNode === editor) return getChipAfterEditorOffset(editor, focusOffset, isChip);
  if (focusNode.nodeType !== Node.TEXT_NODE) return null;

  const text = getNodeText(focusNode);
  return focusOffset === text.length && isChip(focusNode.nextSibling) ? focusNode.nextSibling : null;
};

const getChipBeforeEditorOffset = (
  editor: HTMLElement,
  offset: number,
  isChip: AtomicChipPredicate,
): HTMLElement | null => {
  const previous = editor.childNodes[offset - 1];
  return getStandaloneAtomicChipCaretSpacer(previous, isChip)?.chip ?? (isChip(previous) ? previous : null);
};

const getChipAfterEditorOffset = (
  editor: HTMLElement,
  offset: number,
  isChip: AtomicChipPredicate,
): HTMLElement | null => {
  const next = editor.childNodes[offset];
  return isChip(next) ? next : null;
};

const getChipBeforeTextSelection = ({
  node,
  focusOffset,
  isChip,
}: {
  node: Text;
  focusOffset: number;
  isChip: AtomicChipPredicate;
}): HTMLElement | null => {
  if (focusOffset !== 0) return null;

  const previous = node.previousSibling;
  return getStandaloneAtomicChipCaretSpacer(previous, isChip)?.chip ?? (isChip(previous) ? previous : null);
};

const setCaretBeforeAtomicChip = (selection: Selection, chip: HTMLElement): void => {
  const previous = chip.previousSibling;
  if (previous?.nodeType === Node.TEXT_NODE) {
    setCaretAt(selection, previous, getNodeText(previous).length);
    return;
  }
  setCaretBeforeNode(selection, chip);
};

const setCaretAfterAtomicChip = (selection: Selection, chip: HTMLElement, isChip: AtomicChipPredicate): void => {
  const next = chip.nextSibling;
  if (next?.nodeType === Node.TEXT_NODE) {
    const offset = getAtomicChipCaretSpacer(next, isChip) ? ATOMIC_CHIP_CARET_MARKER.length : 0;
    setCaretAt(selection, next, offset);
    return;
  }
  setCaretAfterNode(selection, chip);
};

const moveCaretRightFromAtomicChip = (
  selection: Selection,
  spacer: AtomicChipCaretSpacer,
  isChip: AtomicChipPredicate,
): void => {
  if (spacer.text.length > ATOMIC_CHIP_CARET_MARKER.length) {
    setCaretAt(selection, spacer.node, getNextTextOffset(spacer.text, ATOMIC_CHIP_CARET_MARKER.length));
    return;
  }

  const next = spacer.node.nextSibling;
  if (next?.nodeType === Node.TEXT_NODE) {
    setCaretAt(selection, next, Math.min(1, getNodeText(next).length));
    return;
  }
  if (isChip(next)) {
    setCaretAfterAtomicChip(selection, next, isChip);
    return;
  }
  setCaretAfterNode(selection, spacer.node);
};

const getNextTextOffset = (text: string, offset: number): number => {
  const codePoint = text.codePointAt(offset);
  if (codePoint === undefined) return offset;
  return Math.min(text.length, offset + (codePoint > 0xffff ? 2 : 1));
};

const getSelectionFocusOffset = (selection: Selection): number => {
  const focusOffset: unknown = selection.focusOffset;
  return typeof focusOffset === 'number' ? focusOffset : 0;
};

const getNodeText = (node: Node | null): string => {
  const text: unknown = node?.textContent;
  return typeof text === 'string' ? text : '';
};

const setCaretAt = (selection: Selection, node: Node, offset: number): void => {
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const setCaretBeforeNode = (selection: Selection, node: Node): void => {
  const range = document.createRange();
  range.setStartBefore(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const setCaretAfterNode = (selection: Selection, node: Node): void => {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};
